import React, {
  useState,
  FC,
  useEffect,
  CSSProperties,
  useCallback,
  useRef,
  SyntheticEvent,
  useMemo
} from "react";
import {
  Editor as DraftEditor,
  EditorState,
  RichUtils,
  DraftEditorCommand,
  getVisibleSelectionRect,
  DraftHandleValue,
  Modifier,
  ContentState,
  SelectionState
} from "draft-js";
import classNames from "classnames";

import { stateFromHTML } from "draft-js-import-html";
import { stateToHTML } from "draft-js-export-html";

import "./draft.css";
import { ContextMenuEvent } from "composants/common";
import { Portal } from "composants/Portal";
import { PluginObject } from "./plugins/pluginTypes";
import autoListPlugin from "./plugins/autoList";
import blockBreakoutPlugin from "./plugins/blockBreakout";
import draftDefaultPlugin from "./plugins/defaultPlugins";
import speechDictationPlugin from "./plugins/speechDictation";
import { useTranslation } from "react-i18next";
import { Notice } from "@axin-org/comet";
import { TextWritingAction } from "api/textWritingAssist";
import { isAnalyzableDocumentFile } from "api/documentAnalysis";
import AiWritingAssistModal from "./AiWritingAssistModal";
import AiPdfSummaryModal from "./AiPdfSummaryModal";
import ToolbarButton from "./ToolbarButton";
import toaster from "composants/toaster/toaster";

export interface EditorProps {
  toolbar?: "TOP" | "BOTTOM" | "HOVER";
  className?: string;
  style?: CSSProperties;
  name?: string;
  value: string;
  disabled?: boolean;
  readonly?: boolean;
  minLength?: number;
  maxLength?: number;
  /** Active la barre d'actions IA (défaut: true) */
  aiAssist?: boolean;
  onContextMenu?: ContextMenuEvent;
  plugins?: PluginObject[];
  onFocus?(e: SyntheticEvent): void;
  onBlur?(e: SyntheticEvent): void;
  onValueChange?(name: string | undefined, html: string): void;
}

type AiAssistSession = {
  action: TextWritingAction;
  sourceText: string;
  selection: SelectionState;
};

// prettier-ignore
export const COLORS = [
  '#B80000', '#DB3E00', '#FCCB00', '#008B02',
  '#006B76', '#1273DE', '#004DCF', '#5300EB',
  '#EB9694', '#FAD0C3', '#FEF3BD', '#C1E1C5',
  '#BEDADC', '#C4DEF6', '#BED3F3', '#D4C4FB',
];

const styleMap = {};
const styleMapConvert = {};

COLORS.map((c, i) => {
  // font color
  styleMap[`color-${c.replace("#", "")}`] = { color: c };
  styleMapConvert[`color-${c.replace("#", "")}`] = {
    style: {
      color: c
    }
  };

  // background color
  styleMap[`background-${c.replace("#", "")}`] = { backgroundColor: c };
  styleMapConvert[`background-${c.replace("#", "")}`] = {
    style: {
      "background-color": c
    }
  };
});

const OPTIONS_CONVERT_HTML = {
  inlineStyles: styleMapConvert
};

const isHtmlNull = (html: string) => {
  return html === "<p><br></p>" || html === "<p></p>";
};

function rgbToHex(r: string, g: string, b: string) {
  return (
    "#" +
    [r, g, b]
      .map(x => {
        const hex = parseInt(x).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
}

const RGB_REGEX = new RegExp("rgb\\((\\d+),\\s{0,1}(\\d+),\\s{0,1}(\\d+)\\)");
function getHex(rgb: string) {
  const result = RGB_REGEX.exec(rgb);

  if (result) {
    return rgbToHex(result[1], result[2], result[3]);
  }
  return null;
}

const defaultPlugins: PluginObject[] = [
  autoListPlugin(),
  blockBreakoutPlugin(),
  speechDictationPlugin()
];

function convertFromHTML(html: string) {
  return stateFromHTML(html, {
    customInlineFn: (element, inlineCreators) => {
      const style = (element as any).style as CSSStyleDeclaration;
      if (style) {
        if (style.color) {
          const hex = getHex(style.color);
          if (hex) {
            return inlineCreators.Style("color-" + hex.substr(1).toUpperCase());
          }
        }

        if (style.backgroundColor) {
          const hex = getHex(style.backgroundColor);
          if (hex) {
            return inlineCreators.Style("background-" + hex.substr(1).toUpperCase());
          }
        }
      }

      return null;
    }
  });
}

function calculatePositionHoverMenu(
  rect: ClientRect,
  toolbarRect: ClientRect,
  toolbarDomInstance: HTMLDivElement
) {
  let collisions = {
    top: rect.top - toolbarRect.height < 0,
    right: document.documentElement.clientWidth < rect.left + toolbarRect.width,
    bottom: window.innerHeight < rect.top + toolbarRect.height,
    left: rect.left - toolbarRect.width < 0
  };

  const directionRight = collisions.right && !collisions.left;
  const directionUp = collisions.bottom && !collisions.top;

  // The toolbar shouldn't be positioned directly on top of the selected text,
  // but rather with a small offset so the caret doesn't overlap with the text.
  const extraTopOffset = -5;
  const position: CSSProperties = {};

  position.top = directionUp
    ? rect.top - toolbarRect.height + window.scrollY
    : rect.top + window.pageYOffset - toolbarDomInstance.offsetHeight + extraTopOffset;
  position.left = directionRight
    ? rect.right - toolbarRect.width + window.scrollX
    : rect.left +
    toolbarRect.width / 2 +
    (window.pageXOffset - toolbarDomInstance.offsetWidth / 2) +
    rect.width / 2;

  return position;
}

const isValidChange = (html: string, value: string) => {
  const normalizedHtml = isHtmlNull(html) ? "" : html;
  const normalizedValue = value == null ? "" : value;
  return normalizedHtml !== normalizedValue;
};

function hasFileDrag(event: React.DragEvent): boolean {
  return Array.from(event.dataTransfer.types).includes("Files");
}

function getFirstDroppedFile(dataTransfer: DataTransfer): File | null {
  if (dataTransfer.files?.length) {
    return dataTransfer.files[0];
  }
  const item = Array.from(dataTransfer.items || []).find(entry => entry.kind === "file");
  return item?.getAsFile() ?? null;
}

function blobToFile(blob: Blob, fallbackName = "document.pdf"): File {
  if (blob instanceof File) {
    return blob;
  }
  return new File([blob], fallbackName, { type: blob.type || "application/octet-stream" });
}

const Editor: FC<EditorProps> = ({
  plugins: userPlugins = defaultPlugins,
  toolbar = "TOP",
  className,
  name,
  style,
  disabled,
  readonly,
  value,
  minLength = 0,
  maxLength,
  aiAssist = true,
  onValueChange: onChangeParent,
  onFocus,
  onBlur,
  onContextMenu
}) => {
  const { t, i18n } = useTranslation();

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const draftEditorRef = useRef<DraftEditor | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [aiSession, setAiSession] = useState<AiAssistSession | null>(null);
  const [pdfSummaryOpen, setPdfSummaryOpen] = useState(false);
  const [pdfDroppedFile, setPdfDroppedFile] = useState<File | null>(null);
  const fileDragCountRef = useRef(0);
  const [fileDragActive, setFileDragActive] = useState(false);
  const [state, setState] = useState(() => {
    if (value !== null && value !== undefined) {
      const state = convertFromHTML(value);
      return EditorState.createWithContent(state);
    } else {
      return EditorState.createEmpty();
    }
  });

  const plugins = useMemo(() => {
    return [...userPlugins, draftDefaultPlugin()];
  }, [userPlugins]);

  const [overlayPosition, setOverlayPosition] = useState<CSSProperties | undefined>();

  const oldValue = useRef(value);
  useEffect(() => {
    const htmlState = stateToHTML(state.getCurrentContent());
    if (value === oldValue.current) {
      return;
    }

    const effectivelyEmpty = (html: string | null | undefined) =>
      !html || html === "<p><br></p>" || html === "<p></p>";

    if (effectivelyEmpty(value) && effectivelyEmpty(htmlState)) {
      // Les deux côtés sont vides : éviter de recréer un EditorState avec une nouvelle clé
      // de bloc (isHtmlNull normalise "" vs "<p><br></p>"), ce qui désynchronise le curseur
    } else if (value !== htmlState && value !== null && value !== undefined) {
      const state = convertFromHTML(value);
      setState(EditorState.createWithContent(state));
    } else if (
      oldValue.current !== null &&
      htmlState !== null &&
      htmlState !== undefined &&
      (value === undefined || value === null)
    ) {
      setState(EditorState.createEmpty());
    }
    oldValue.current = value;
  }, [state, value]);

  const notifyParentChange = useCallback(
    (state: EditorState) => {
      if (disabled || readonly) {
        return;
      }
      const html = stateToHTML(state.getCurrentContent(), OPTIONS_CONVERT_HTML);
      if (onChangeParent && isValidChange(html, value)) {
        onChangeParent(name, isHtmlNull(html) ? "" : html);
      }
    },
    [name, onChangeParent, value, disabled, readonly]
  );

  const onSelectionChanged = useCallback(
    function onSelectionChanged() {
      if (toolbar !== "HOVER") {
        return;
      }
      // need to wait a tick for window.getSelection() to be accurate
      // when focusing editor with already present selection
      setTimeout(() => {
        if (!toolbarRef.current) return;

        // The editor root should be two levels above the node from
        // `getEditorRef`. In case this changes in the future, we
        // attempt to find the node dynamically by traversing upwards.
        if (!draftEditorRef.current) return;

        const selectionRect = getVisibleSelectionRect(window);
        if (!selectionRect) return;

        const rect = selectionRect;

        const toolbarRect = toolbarRef.current.getBoundingClientRect();

        const position = calculatePositionHoverMenu(
          rect as ClientRect,
          toolbarRect,
          toolbarRef.current
        );
        setOverlayPosition(position);
      }, 300);
    },
    [toolbar]
  );

  function beforeChange() {
    // Gestion du sjcoOptions Max value
    if (maxLength && value && maxLength < value.length) {
      return "handled";
    }
    return "not-handled";
  }

  const onChange = useCallback(
    function onChange(editorState: EditorState | null) {
      if (editorState) {
        setState(editorState);
        notifyParentChange(editorState);
        onSelectionChanged();
      }
    },
    [notifyParentChange, onSelectionChanged]
  );

  const pluginContext = useMemo(() => {
    return { setEditorState: onChange };
  }, [onChange]);

  let keyCommandPlugins = useMemo(() => {
    let functions: Required<PluginObject>["handleKeyCommand"][] = [];

    for (let plugin of plugins) {
      if (plugin.handleKeyCommand) {
        functions.push(plugin.handleKeyCommand);
      }
    }
    return functions;
  }, [plugins]);

  const handleReturnsPlugins = useMemo(() => {
    let functions: Required<PluginObject>["handleReturn"][] = [];

    for (let plugin of plugins) {
      if (plugin.handleReturn) {
        functions.push(plugin.handleReturn);
      }
    }
    return functions;
  }, [plugins]);

  const keyBindingFnPlugins = useMemo(() => {
    let functions: Required<PluginObject>["keyBindingFn"][] = [];

    for (let plugin of plugins) {
      if (plugin.keyBindingFn) {
        functions.push(plugin.keyBindingFn);
      }
    }
    return functions;
  }, [plugins]);

  function handleKeyCommand(command: DraftEditorCommand, editorState: EditorState) {
    for (let pluginCommand of keyCommandPlugins) {
      let result = pluginCommand(command, editorState, pluginContext);

      if (result === "handled") {
        return "handled";
      }
    }

    return "not-handled";
  }

  function handleReturn(e: React.KeyboardEvent<{}>, editorState: EditorState): DraftHandleValue {
    for (let pluginHandleReturn of handleReturnsPlugins) {
      let result = pluginHandleReturn(e, editorState, pluginContext);
      if (result === "handled") {
        return "handled";
      }
    }
    return "not-handled";
  }

  function keyBindingFn(e: React.KeyboardEvent<{}>): DraftEditorCommand | null {
    for (let pluginKeyBindingFn of keyBindingFnPlugins) {
      let result = pluginKeyBindingFn(e);

      if (result !== null) {
        return result as any;
      }
    }

    return null;
  }

  function handleInlineStyles(style: string) {
    const newState = RichUtils.toggleInlineStyle(state, style);
    if (newState) {
      onChange(newState);
    }
  }

  function getAiSourceText(editorState: EditorState): { text: string; selection: SelectionState } {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    if (!selection.isCollapsed()) {
      const startKey = selection.getStartKey();
      const endKey = selection.getEndKey();
      const startOffset = selection.getStartOffset();
      const endOffset = selection.getEndOffset();

      if (startKey === endKey) {
        const block = content.getBlockForKey(startKey);
        return {
          text: block.getText().slice(startOffset, endOffset),
          selection
        };
      }

      const parts: string[] = [];
      let inRange = false;
      content.getBlockMap().forEach((block, key) => {
        if (!block || !key) return;
        if (key === startKey) {
          inRange = true;
          parts.push(block.getText().slice(startOffset));
          return;
        }
        if (key === endKey) {
          parts.push(block.getText().slice(0, endOffset));
          inRange = false;
          return;
        }
        if (inRange) {
          parts.push(block.getText());
        }
      });
      return { text: parts.join("\n"), selection };
    }
    return { text: content.getPlainText(), selection };
  }

  function openAiAssist(action: TextWritingAction) {
    const { text, selection } = getAiSourceText(state);
    if (!text.trim()) {
      return;
    }
    setAiSession({ action, sourceText: text, selection });
  }

  function applyAiResult(result: string) {
    if (!aiSession) return;

    const selection = aiSession.selection;
    let nextState: EditorState;

    if (!selection.isCollapsed()) {
      const withSelection = EditorState.acceptSelection(state, selection);
      const newContent = Modifier.replaceText(
        withSelection.getCurrentContent(),
        selection,
        result
      );
      nextState = EditorState.push(withSelection, newContent, "insert-characters");
    } else {
      const newContent = ContentState.createFromText(result);
      nextState = EditorState.push(state, newContent, "change-block-data");
      nextState = EditorState.moveFocusToEnd(nextState);
    }

    onChange(nextState);
    setAiSession(null);
  }

  function applyPdfSummary(html: string) {
    if (!html.trim()) {
      setPdfDroppedFile(null);
      setPdfSummaryOpen(false);
      return;
    }

    const currentHtml = stateToHTML(state.getCurrentContent(), OPTIONS_CONVERT_HTML);
    const empty = isHtmlNull(currentHtml) || !currentHtml.trim();
    const combinedHtml = empty ? html : `${currentHtml}${html}`;
    const newContent = convertFromHTML(combinedHtml);
    const nextState = EditorState.moveFocusToEnd(
      EditorState.push(state, newContent, "insert-fragment")
    );
    onChange(nextState);
    setPdfDroppedFile(null);
    setPdfSummaryOpen(false);
  }

  const focusEditor = useCallback(() => {
    draftEditorRef.current?.focus();
  }, []);

  const currentInlineStyles = state.getCurrentInlineStyle();
  const plainText = state.getCurrentContent().getPlainText().trim();
  const isEditorDisabled = !!readonly || !!disabled;
  const aiDisabled = isEditorDisabled || !plainText;
  const pdfAiDisabled = isEditorDisabled;
  const canDropFiles = aiAssist && !isEditorDisabled;

  const openPdfSummary = useCallback(
    (file?: File | null) => {
      if (!canDropFiles && file) {
        return;
      }
      if (isEditorDisabled) {
        return;
      }
      if (file && !isAnalyzableDocumentFile(file)) {
        toaster.error(
          t("commun_ia_format_document", {
            defaultValue: "Seuls les fichiers PDF et Word (.docx, .doc) sont acceptés"
          })
        );
        return;
      }
      setPdfDroppedFile(file ?? null);
      setPdfSummaryOpen(true);
    },
    [canDropFiles, isEditorDisabled, t]
  );

  const resetFileDrag = useCallback(() => {
    fileDragCountRef.current = 0;
    setFileDragActive(false);
  }, []);

  const handleEditorFileDrop = useCallback(
    (file: File | null) => {
      resetFileDrag();
      if (!file) return;
      openPdfSummary(file);
    },
    [openPdfSummary, resetFileDrag]
  );

  const handleDroppedFiles = useCallback(
    (_selection: SelectionState, files: Blob[]): DraftHandleValue => {
      if (!canDropFiles) {
        return "not-handled";
      }
      const file = files[0] ? blobToFile(files[0]) : null;
      if (!file) {
        return "not-handled";
      }
      handleEditorFileDrop(file);
      return "handled";
    },
    [canDropFiles, handleEditorFileDrop]
  );

  const aiToolbarButtons =
    aiAssist && toolbar !== "HOVER" ? (
      <>
        <span
          style={{
            display: "inline-block",
            width: 1,
            height: "1.5em",
            background: "#dedede",
            margin: "0 0.5em",
            verticalAlign: "middle"
          }}
        />
        <ToolbarButton
          icon="language"
          isActive={false}
          disabled={aiDisabled}
          title={t("commun_ia_traduire", { defaultValue: "Traduire" })}
          onClick={() => openAiAssist("translate")}
        />
        <ToolbarButton
          icon="chart-bar"
          isActive={false}
          disabled={aiDisabled}
          title={t("commun_ia_analyser", { defaultValue: "Analyser le texte" })}
          onClick={() => openAiAssist("analyze")}
        />
        <ToolbarButton
          icon="expand-alt"
          isActive={false}
          disabled={aiDisabled}
          title={t("commun_ia_plus_long", { defaultValue: "Écrire plus long" })}
          onClick={() => openAiAssist("longer")}
        />
        <ToolbarButton
          icon="compress-alt"
          isActive={false}
          disabled={aiDisabled}
          title={t("commun_ia_plus_court", { defaultValue: "Écrire plus court" })}
          onClick={() => openAiAssist("shorter")}
        />
        <ToolbarButton
          icon="comment-dots"
          isActive={false}
          disabled={aiDisabled}
          title={t("commun_ia_expliquer", { defaultValue: "Expliquer" })}
          onClick={() => openAiAssist("explain")}
        />
        <ToolbarButton
          icon="spell-check"
          isActive={false}
          disabled={aiDisabled}
          title={t("commun_ia_corriger", { defaultValue: "Corriger l'orthographe" })}
          onClick={() => openAiAssist("spellcheck")}
        />
        <ToolbarButton
          icon="file-alt"
          isActive={false}
          disabled={pdfAiDisabled}
          title={t("commun_ia_synthese_document", { defaultValue: "Synthèse PDF / Word" })}
          onClick={() => openPdfSummary()}
        />
      </>
    ) : null;

  const pluginToolbarButtons =
    toolbar !== "HOVER"
      ? plugins.map((plugin, index) => {
          if (!plugin.Toolbar) return null;
          const PluginToolbar = plugin.Toolbar;
          return (
            <PluginToolbar
              key={index}
              editorState={state}
              setEditorState={onChange}
              focusEditor={focusEditor}
              disabled={disabled}
              readonly={readonly}
              language={i18n.language}
            />
          );
        })
      : null;

  const toolbarComponent =
    toolbar !== "HOVER" ? (
      <>
        <ToolbarButton
          icon="bold"
          isActive={currentInlineStyles.has("BOLD")}
          disabled={isEditorDisabled}
          onClick={() => handleInlineStyles("BOLD")}
        />
        <ToolbarButton
          icon="italic"
          isActive={currentInlineStyles.has("ITALIC")}
          disabled={isEditorDisabled}
          onClick={() => handleInlineStyles("ITALIC")}
        />
        <ToolbarButton
          icon="underline"
          isActive={currentInlineStyles.has("UNDERLINE")}
          disabled={isEditorDisabled}
          onClick={() => handleInlineStyles("UNDERLINE")}
        />
        {aiToolbarButtons}
        {pluginToolbarButtons}
      </>
    ) : null;

  return (
    <>
      {toolbar === "TOP" && (
        <div className="mt-7" style={{ borderTop: "1px solid #dedede", padding: "0.4em" }}>
          {toolbarComponent}
        </div>
      )}
      <div
        ref={wrapperRef}
        className={classNames("editor-draft", className)}
        style={{ ...style, position: style?.position ?? "relative" }}
        onContextMenu={onContextMenu}
        onClick={() => {
          draftEditorRef.current && draftEditorRef.current.focus();
        }}
        onDragEnter={e => {
          if (!canDropFiles || !hasFileDrag(e)) return;
          e.preventDefault();
          e.stopPropagation();
          fileDragCountRef.current += 1;
          setFileDragActive(true);
        }}
        onDragOver={e => {
          if (!hasFileDrag(e)) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = canDropFiles ? "copy" : "none";
        }}
        onDragLeave={e => {
          if (!hasFileDrag(e)) return;
          e.preventDefault();
          e.stopPropagation();
          fileDragCountRef.current = Math.max(0, fileDragCountRef.current - 1);
          if (fileDragCountRef.current === 0) {
            setFileDragActive(false);
          }
        }}
        onDrop={e => {
          if (!hasFileDrag(e)) return;
          const alreadyHandled = e.defaultPrevented;
          e.preventDefault();
          e.stopPropagation();
          if (alreadyHandled) return;
          handleEditorFileDrop(getFirstDroppedFile(e.dataTransfer));
        }}
      >
        {fileDragActive && canDropFiles && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              border: "2px dashed #3273dc",
              background: "rgba(245, 248, 255, 0.92)",
              color: "#3273dc",
              fontWeight: 600,
              textAlign: "center",
              padding: "1rem"
            }}
          >
            {t("commun_ia_choisir_document", {
              defaultValue: "Déposez un PDF ou un Word (.docx) pour l'analyser"
            })}
          </div>
        )}
        <div className="content" style={{ width: "inherit", height: "inherit" }}>
          <DraftEditor
            ref={draftEditorRef}
            editorState={state}
            handleKeyCommand={handleKeyCommand}
            handleReturn={handleReturn}
            handleDroppedFiles={handleDroppedFiles}
            handlePastedFiles={files => handleDroppedFiles(state.getSelection(), files)}
            keyBindingFn={keyBindingFn}
            readOnly={readonly || disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            handleBeforeInput={beforeChange}
            onChange={onChange}
            customStyleMap={styleMap}
            spellCheck
          />
        </div>
      </div>
      <div>
        {minLength !== 0 && value && minLength > value.length && value != "<p><br></p>" && (
          <Notice className="pt-1" intent="warning">
            {t("commun_caractere_min", {
              min: minLength,
              length: value.length
            })}
          </Notice>
        )}
        {maxLength && value && maxLength < value.length && value != "<p><br></p>" && (
          <Notice className="pt-1" intent="warning">
            {t("commun_caractere_max", {
              max: maxLength,
              length: value.length
            })}
          </Notice>
        )}
      </div>
      {toolbar === "BOTTOM" && (
        <div className="mb-7" style={{ borderBottom: "1px solid #dedede", padding: "0.4em" }}>
          {toolbarComponent}
        </div>
      )}
      {toolbar === "HOVER" && (
        <Portal>
          <HoverMenu ref={toolbarRef} position={overlayPosition} editorState={state}>
            <ToolbarButton
              className="is-text"
              icon="bold"
              isActive={currentInlineStyles.has("BOLD")}
              disabled={isEditorDisabled}
              onClick={() => handleInlineStyles("BOLD")}
            />
            <ToolbarButton
              className="is-text"
              icon="italic"
              isActive={currentInlineStyles.has("ITALIC")}
              disabled={isEditorDisabled}
              onClick={() => handleInlineStyles("ITALIC")}
            />
            <ToolbarButton
              className="is-text"
              icon="underline"
              isActive={currentInlineStyles.has("UNDERLINE")}
              disabled={isEditorDisabled}
              onClick={() => handleInlineStyles("UNDERLINE")}
            />
          </HoverMenu>
        </Portal>
      )}
      {aiSession && (
        <AiWritingAssistModal
          action={aiSession.action}
          sourceText={aiSession.sourceText}
          onAccept={applyAiResult}
          onClose={() => setAiSession(null)}
        />
      )}
      {pdfSummaryOpen && (
        <AiPdfSummaryModal
          initialFile={pdfDroppedFile}
          onAccept={applyPdfSummary}
          onClose={() => {
            setPdfDroppedFile(null);
            setPdfSummaryOpen(false);
          }}
        />
      )}
    </>
  );
};

type HoverMenuProps = {
  editorState: EditorState;
  position?: CSSProperties;
  children?: React.ReactNode;
};

const HoverMenu = React.forwardRef<HTMLDivElement, HoverMenuProps>(
  ({ editorState, position, children }, ref) => {
    const [isVisible, setIsVisible] = useState<boolean>();
    const selection = editorState.getSelection();
    const isCollapsed = selection.isCollapsed();
    const hasFocus = selection.getHasFocus();
    useEffect(() => {
      const isVisibleSelection = !isCollapsed && hasFocus;

      let timeout: NodeJS.Timeout | undefined;
      if (isVisible !== isVisibleSelection) {
        timeout = setTimeout(() => {
          setIsVisible(isVisibleSelection);
        }, 100);
      }

      return () => {
        timeout && clearTimeout(timeout);
      };
    }, [isCollapsed, hasFocus, isVisible]);

    function getStyleHover(): CSSProperties {
      const style: CSSProperties = { ...position };

      if (isVisible) {
        style.opacity = 1;
        style.transform = "translate(-50%) scale(1)";
        style.transition = "transform 0.15s cubic-bezier(.3,1.2,.2,1)";
      } else {
        style.opacity = 0;
        style.transform = "translate(-50%) scale(0)";
      }

      return style;
    }

    return (
      // <Portal>
      <div ref={ref} className="editor-draft-toolbar-hover" style={getStyleHover()}>
        {children}
      </div>
      // </Portal>
    );
  }
);

export default Editor;
